import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ShoppingBag, Trash2, X } from 'lucide-react'
import { Drawer } from '@/components/generals/dialog'
import { buttonStyles } from '@/components/generals/button'
import { applyDiscount, formatRupiah } from '@/lib/format'

import { useCart } from './cart-context'
import { CartThumb } from './cart-thumb'
import { QuantityStepper } from './quantity-stepper'

export const CartDrawer: FC = (): ReactNode => {
    const cart = useCart()
    const savings = cart.subtotal - cart.total

    return (
        <Drawer open={cart.drawerOpen} onClose={cart.closeDrawer} labelledBy='judul-keranjang'>
            <div className='flex items-center justify-between border-b-2 border-brand-choco/10 px-5 py-4'>
                <h2 id='judul-keranjang' className='flex items-center gap-2 font-heading text-xl font-bold text-brand-choco'>
                    <ShoppingBag className='size-5' aria-hidden='true' />
                    Keranjang Belanja
                    <span className='rounded-full bg-brand-yellow px-2.5 py-0.5 text-sm'>{cart.count}</span>
                </h2>
                <button type='button' onClick={cart.closeDrawer} className={buttonStyles({ variant: 'ghost', size: 'icon' })} aria-label='Tutup keranjang'>
                    <X className='size-6' />
                </button>
            </div>

            {cart.items.length === 0 ? (
                <div className='flex flex-1 flex-col items-center justify-center px-8 text-center'>
                    <div className='flex size-24 items-center justify-center rounded-full border-2 border-dashed border-brand-choco/30 bg-brand-cream'>
                        <ShoppingBag className='size-10 text-brand-brown' aria-hidden='true' />
                    </div>
                    <p className='mt-5 font-heading text-xl font-bold text-brand-choco'>
                        Keranjangmu masih kosong
                    </p>
                    <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                        Yuk pilih keripik favoritmu dulu. Ada promo seru yang menunggu!
                    </p>
                    <Link href='/produk' onClick={cart.closeDrawer} className={buttonStyles({ variant: 'primary', size: 'lg', className: 'mt-6' })}>
                        Mulai Belanja
                    </Link>
                </div>
            ) : (
                <>
                    <ul className='flex-1 divide-y-2 divide-brand-choco/5 overflow-y-auto px-5 scrollbar-thin'>
                        {cart.items.map(item => {
                            const finalPrice = applyDiscount(item.price, item.discountPercent)

                            return (
                                <li key={item.key} className='flex gap-4 py-4'>
                                    <CartThumb
                                        productId={item.productId}
                                        name={item.name}
                                        image={item.image}
                                        packageColor={item.packageColor}
                                        accentColor={item.accentColor}
                                        className='size-20'
                                    />
                                    <div className='min-w-0 flex-1'>
                                        <div className='flex items-start justify-between gap-2'>
                                            <Link
                                                href={`/produk/${item.slug}`}
                                                onClick={cart.closeDrawer}
                                                className='font-heading font-semibold leading-snug text-brand-choco hover:text-brand-orange'
                                            >
                                                <span data-mdlfy-cms={`products:${item.productId}:name`}>{item.name}</span>
                                            </Link>
                                            <button
                                                type='button'
                                                onClick={() => cart.remove(item.key)}
                                                className='-mr-1 inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
                                                aria-label={`Hapus ${item.name}`}
                                            >
                                                <Trash2 className='size-4' />
                                            </button>
                                        </div>
                                        <p className='mt-0.5 text-sm text-muted-foreground'>
                                            <span data-mdlfy-cms={`product_sizes:${item.sizeId}:label`}>{item.sizeLabel}</span>
                                            {item.variant && (
                                                <>
                                                    , <span>{item.variant}</span>
                                                </>
                                            )}
                                        </p>
                                        <div className='mt-2 flex items-center justify-between gap-3'>
                                            <QuantityStepper
                                                size='sm'
                                                value={item.quantity}
                                                max={item.maxStock > 0 ? Math.min(item.maxStock, 99) : 99}
                                                onChange={quantity => cart.update(item.key, quantity)}
                                                label={`Jumlah ${item.name}`}
                                            />
                                            <div className='text-right'>
                                                {item.discountPercent > 0 && (
                                                    <p className='text-xs text-muted-foreground line-through'>
                                                        {formatRupiah(item.price * item.quantity)}
                                                    </p>
                                                )}
                                                <p className='font-heading font-bold text-brand-choco'>
                                                    {formatRupiah(finalPrice * item.quantity)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            )
                        })}
                    </ul>

                    <div className='border-t-2 border-brand-choco/10 bg-white px-5 py-5'>
                        {savings > 0 && (
                            <div className='mb-2 flex justify-between text-sm font-semibold text-brand-green'>
                                <span>Kamu hemat</span>
                                <span>{formatRupiah(savings)}</span>
                            </div>
                        )}
                        <div className='flex items-end justify-between'>
                            <span className='text-sm font-medium text-muted-foreground'>Total belanja</span>
                            <span className='font-heading text-2xl font-bold text-brand-choco'>{formatRupiah(cart.total)}</span>
                        </div>
                        <p className='mt-1 text-xs text-muted-foreground'>Belum termasuk ongkos kirim.</p>
                        <div className='mt-4 grid gap-2.5'>
                            <Link href='/keranjang' onClick={cart.closeDrawer} className={buttonStyles({ variant: 'primary', size: 'lg', className: 'w-full' })}>
                                Lanjut ke Checkout
                            </Link>
                            <button type='button' onClick={cart.closeDrawer} className={buttonStyles({ variant: 'outline', size: 'md', className: 'w-full' })}>
                                Lanjut Belanja
                            </button>
                        </div>
                    </div>
                </>
            )}
        </Drawer>
    )
}
